/** Stacking for floating overlays: above the app's fixed 1200 navigation rail, at MUI's modal layer. */
export const OVERLAY_MIN_Z_INDEX = 1300;
/** Viewports at or below this width are the phone layout; the navigation rail covers the left edge there. */
export const NARROW_VIEWPORT_MAX_WIDTH = 640;
const RAIL_FALLBACK_WIDTH = 49;
const EDGE_GAP = 8;

interface ClampInput {
  /** Current left edge of the overlay in viewport coordinates. */
  left: number;
  width: number;
  viewportWidth: number;
  /** Width of the fixed navigation rail on the left. */
  railWidth: number;
}

/** Left edge that keeps the overlay clear of the rail (rail + 8px) and inside the right edge of the viewport. */
export const clampOverlayLeft = ({ left, width, viewportWidth, railWidth }: ClampInput): number => {
  const minLeft = railWidth + EDGE_GAP;
  const maxLeft = viewportWidth - width - EDGE_GAP;
  return Math.max(minLeft, Math.min(left, maxLeft));
};

const railWidthPx = (): number => {
  const paper = document.querySelector<HTMLElement>('.MuiDrawer-docked .MuiDrawer-paper, .MuiDrawer-paper');
  const width = paper ? paper.getBoundingClientRect().right : 0;
  return width > 0 ? width : RAIL_FALLBACK_WIDTH;
};

/**
 * Keeps a PrimeReact overlay above the navigation rail and, on a narrow viewport, inside the area the rail does not
 * cover. Desktop widths only get the stacking; the position PrimeReact computed is left alone.
 */
export const keepOverlayClear = (overlay: HTMLElement | null | undefined, target?: Element | null): void => {
  if (!overlay) {
    return;
  }
  const z = Number.parseInt(overlay.style.zIndex || '0', 10);
  if (!(z >= OVERLAY_MIN_Z_INDEX)) {
    overlay.style.setProperty('z-index', String(OVERLAY_MIN_Z_INDEX), 'important');
  }
  const viewportWidth = window.innerWidth;
  if (viewportWidth > NARROW_VIEWPORT_MAX_WIDTH) {
    return;
  }
  const railWidth = railWidthPx();
  const minLeft = railWidth + EDGE_GAP;
  // The panel may not be wider than the visible strip, so its content scrolls instead of running under the rail.
  overlay.style.maxWidth = `${Math.max(0, viewportWidth - minLeft - EDGE_GAP)}px`;
  const rect = overlay.getBoundingClientRect();
  const left = clampOverlayLeft({ left: rect.left, width: rect.width, viewportWidth, railWidth });
  const delta = left - rect.left;
  if (Math.abs(delta) >= 0.5) {
    const current = Number.parseFloat(overlay.style.left || '0') || 0;
    overlay.style.left = `${current + delta}px`;
  }
  if (target) {
    const arrowLeft = Math.max(0, target.getBoundingClientRect().left - left);
    overlay.style.setProperty('--overlayArrowLeft', `${arrowLeft}px`);
  }
};

/**
 * Runs `apply` once the overlay element exists (PrimeReact mounts and positions it right after toggle) and again when
 * the show transition ends, so the panel never opens under the rail.
 */
export const scheduleOverlayClear = (getOverlay: () => HTMLElement | null | undefined, target?: Element | null): void => {
  const run = () => keepOverlayClear(getOverlay(), target);
  if (typeof window.requestAnimationFrame === 'function') {
    window.requestAnimationFrame(run);
  } else {
    window.setTimeout(run, 0);
  }
};
